// A página dos avisos (IDEA-044).
//
// Aqui vive a metade GUARDADA da funcionalidade. A outra — "é a tua vez" — não
// se guarda em lado nenhum: é lida das rondas a cada pedido, e é isso que a faz
// apagar-se sozinha assim que se joga.
//
// A diferença aparece nesta página e vale a pena não a esconder: **marcar um
// aviso como lido não tira a ronda da lista de quem espera por ti**, e jogar a
// ronda não apaga o aviso. Um diz o que aconteceu, o outro diz o que falta
// fazer. Por isso o cartão do topo — as rondas à espera — vem primeiro e é o
// único com botões: é o que ainda se pode fazer alguma coisa a respeito.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { motion, useReducedMotion } from "framer-motion";
import type { Notification } from "@historia/shared/contracts";
import { GAME_LABELS } from "@historia/shared/validation";
import {
  AFTER_NOTIFICATION_CHANGE,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/api/mutations";
import { useNotifications, usePendingGames } from "@/api/queries";
import { Consulta } from "@/components/ui/EstadoConsulta";
import { useTitle } from "@/components/ui/useTitle";

export function Notificacoes() {
  useTitle("Avisos");
  const query = useNotifications();
  const pendentes = usePendingGames();
  const queryClient = useQueryClient();

  function invalidar() {
    for (const key of AFTER_NOTIFICATION_CHANGE) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  }

  const lerTodos = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidar,
  });
  const lerUm = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: invalidar,
  });

  const aEspera = pendentes.data ?? [];

  return (
    <main className="mx-auto max-w-2xl px-4 py-8 sm:px-5">
      <header className="mb-6 text-center">
        <h1 className="font-titulo text-3xl text-tinta-800 sm:text-4xl">Avisos</h1>
        <p className="mt-1 text-sm text-tinta-600">O que aconteceu enquanto não estavas por cá.</p>
      </header>

      {/* As rondas à espera. Primeiro, porque é a única coisa aqui que se pode
          resolver — o resto já aconteceu. Some quando não há nenhuma, como
          todos os widgets do Início. */}
      {aEspera.length > 0 && (
        <section className="mb-6 rounded-2xl border border-ouro/40 bg-creme-50 p-5 shadow-[0_14px_34px_-22px_rgba(201,162,39,0.8)]">
          <h2 className="text-[11px] font-semibold uppercase tracking-wide text-tinta-500">
            É a tua vez
          </h2>
          <ul className="mt-2 flex flex-col gap-1.5">
            {aEspera.map((jogo) => (
              <li key={jogo.jogo}>
                <Link
                  to={`/jogos/${jogo.jogo}`}
                  className="font-semibold text-tinta-800 transition-colors hover:text-ouro-600"
                >
                  {GAME_LABELS[jogo.jogo]} →
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Consulta query={query} linhas={3}>
        {(dados) => (
          <>
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm text-tinta-600">
                {dados.naoLidas > 0
                  ? `${dados.naoLidas} por ler`
                  : dados.notificacoes.length > 0
                    ? "Está tudo lido."
                    : ""}
              </p>
              {dados.naoLidas > 0 && (
                <button
                  type="button"
                  onClick={() => lerTodos.mutate()}
                  disabled={lerTodos.isPending}
                  className="shrink-0 text-xs font-semibold text-ouro-600 transition-colors hover:underline disabled:opacity-60"
                >
                  Marcar tudo como lido
                </button>
              )}
            </div>

            {dados.notificacoes.length === 0 ? (
              <p className="rounded-2xl border border-ouro/20 bg-creme-50/80 p-6 text-center text-sm text-tinta-600">
                Ainda não há avisos. Abre uma ronda e a outra pessoa fica a saber. 💛
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {dados.notificacoes.map((n) => (
                  <Aviso key={n.id} aviso={n} aoLer={() => lerUm.mutate(n.id)} />
                ))}
              </ul>
            )}
          </>
        )}
      </Consulta>
    </main>
  );
}

function Aviso({ aviso, aoLer }: { aviso: Notification; aoLer: () => void }) {
  const reduzir = useReducedMotion();
  const porLer = aviso.lida_em === null;

  return (
    <motion.li
      initial={reduzir ? { opacity: 0 } : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className={`rounded-2xl border p-4 transition-colors ${
        porLer ? "border-ouro/40 bg-creme-50" : "border-ouro/15 bg-creme-50/60"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* O ponto por ler. Só um ponto — é a diferença entre "isto é novo" e
            "isto já viste", e não precisa de mais nada para se ler. */}
        <span
          aria-hidden
          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${porLer ? "bg-ouro" : "bg-transparent"}`}
        />
        <div className="min-w-0 flex-1">
          {/* Abrir o aviso marca-o lido e leva ao jogo. Duas coisas num toque,
              porque ninguém abre isto para ler e ficar. */}
          <Link
            to={aviso.url}
            onClick={() => porLer && aoLer()}
            className="block"
          >
            <p className="font-semibold text-tinta-800">{aviso.titulo}</p>
            <p className="mt-0.5 text-sm text-tinta-700">{aviso.corpo}</p>
          </Link>
          <div className="mt-1.5 flex items-center gap-3">
            <time dateTime={aviso.criado_em} className="text-xs text-tinta-500">
              {quando(aviso.criado_em)}
            </time>
            {porLer && (
              <button
                type="button"
                onClick={aoLer}
                className="text-xs font-semibold text-ouro-600 transition-colors hover:underline"
              >
                Marcar como lido
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.li>
  );
}

/**
 * "agora", "há 3 h", "ontem", ou a data.
 *
 * Feito à mão em vez de com `Intl.RelativeTimeFormat` porque só há cinco casos
 * e o formatador exigiria escolher a unidade na mesma — que é a parte toda.
 */
function quando(iso: string): string {
  const minutos = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutos < 2) return "agora";
  if (minutos < 60) return `há ${minutos} min`;

  const horas = Math.round(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  if (horas < 48) return "ontem";

  return new Date(iso).toLocaleDateString("pt-PT", {
    day: "numeric",
    month: "long",
  });
}
